/**
 * ActivityEditSheet - Sheet lateral para edição de atividades
 * Permite editar título, descrição e data/hora de atividades manuais
 * Reutilizado por Timeline de Negócio e Timeline de Cliente
 *
 * @version 1.1.0
 * RELEASE: 2026-07-11 v1.1.0 - dealId agora opcional; suporta atividades do cliente
 * RELEASE: 2026-02-09 v1.0.0 - Componente inicial de edição de atividades
 */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Phone, 
  Calendar, 
  Mail, 
  CheckSquare, 
  FileText,
  Loader2,
  Save
} from 'lucide-react';
import { useCrmDealActivities, type DealActivity, type ActivityType } from '@/hooks/useCrmDealActivities';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { ActivityAttachmentsDisplay } from '@/components/crm/deals/ActivityAttachmentsDisplay';

// Schema de validação do formulário de edição
const activityEditSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório').max(100, 'Título muito longo'),
  description: z.string().max(5000, 'Descrição muito longa').optional(),
  scheduled_at: z.string().optional(),
});

type ActivityEditFormData = z.infer<typeof activityEditSchema>;

// Props do componente
// v1.1.0 - Aceita activity de Deal ou Customer; dealId opcional
type EditableActivity = {
  id: string;
  activity_type: string;
  title: string;
  description: string | null;
  scheduled_at: string | null;
  attachments?: Array<any> | null;
};

interface ActivityEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: EditableActivity | null;
  /** dealId opcional — sheet só usa a mutation, que é tenant-scoped */
  dealId?: string | null;
  onSuccess?: () => void;
}

// Configuração visual por tipo de atividade
const activityTypeConfig: Record<string, {
  icon: typeof Phone;
  label: string;
  color: string;
  bgClass: string;
  showSchedule: boolean;
  showRichText: boolean;
}> = {
  call: {
    icon: Phone,
    label: 'Ligação',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
    showSchedule: false,
    showRichText: false,
  },
  meeting: {
    icon: Calendar,
    label: 'Reunião',
    color: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    showSchedule: true,
    showRichText: false,
  },
  email: {
    icon: Mail,
    label: 'E-mail',
    color: 'text-violet-600 dark:text-violet-400',
    bgClass: 'bg-violet-100 dark:bg-violet-900/30',
    showSchedule: false,
    showRichText: false,
  },
  task: {
    icon: CheckSquare,
    label: 'Lembrete',
    color: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    showSchedule: true,
    showRichText: false,
  },
  note: {
    icon: FileText,
    label: 'Nota',
    color: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    showSchedule: false,
    showRichText: true,
  },
};

/**
 * Converte data UTC do banco para formato do input datetime-local
 * O input espera formato YYYY-MM-DDTHH:mm em horário local
 */
function formatForDatetimeLocal(isoDate: string): string {
  const date = new Date(isoDate);
  // Ajusta para timezone local
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Sheet lateral para edição de atividade existente
 */
export function ActivityEditSheet({
  open,
  onOpenChange,
  activity,
  dealId,
  onSuccess
}: ActivityEditSheetProps) {
  // Estados locais
  const [richDescription, setRichDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hook de atividades — apenas para a mutation `updateActivity` (RPC tenant-scoped).
  // enabled:false → sem SELECT; aceita dealId nulo (uso em contexto de cliente).
  const { updateActivity } = useCrmDealActivities(dealId ?? null, { enabled: false });

  // Form
  const form = useForm<ActivityEditFormData>({
    resolver: zodResolver(activityEditSchema),
    defaultValues: {
      title: '',
      description: '',
      scheduled_at: '',
    },
  });

  // Preenche formulário quando a atividade muda
  useEffect(() => {
    if (activity && open) {
      form.reset({
        title: activity.title || '',
        description: activity.description || '',
        scheduled_at: activity.scheduled_at 
          ? formatForDatetimeLocal(activity.scheduled_at) 
          : '',
      });
      setRichDescription(activity.description || '');
    }
  }, [activity, open, form]);

  // Se não tem atividade, não renderiza
  if (!activity) return null;

  // Configuração baseada no tipo
  const config = activityTypeConfig[activity.activity_type] || activityTypeConfig.note;
  const Icon = config.icon;

  // Submit do formulário
  const onSubmit = async (data: ActivityEditFormData) => {
    setIsSubmitting(true);
    
    try {
      // Usa descrição rica para notas, simples para outros tipos
      const description = config.showRichText ? richDescription : data.description;

      await updateActivity.mutateAsync({
        activity_id: activity.id,
        title: data.title,
        description: description || null,
        scheduled_at: data.scheduled_at || null,
      });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Toast já é exibido pelo hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className={cn(
          "p-0 flex flex-col",
          config.showRichText 
            ? "w-[480px] sm:w-[560px]" 
            : "w-[360px] sm:w-[420px]"
        )}
      >
        {/* Header */}
        <SheetHeader className="p-4 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', config.bgClass)}>
              <Icon className={cn('h-5 w-5', config.color)} />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-base">Editar Atividade</SheetTitle>
              <SheetDescription className="text-xs">
                Altere os detalhes da atividade
              </SheetDescription>
            </div>
            {/* Badge do tipo (não editável) */}
            <Badge variant="secondary" className="text-xs">
              {config.label}
            </Badge>
          </div>
        </SheetHeader>

        {/* Conteúdo com scroll */}
        <ScrollArea className="flex-1">
          <form id="activity-edit-form" onSubmit={form.handleSubmit(onSubmit)} className="p-4 space-y-4">
            {/* Campo Título */}
            <div className="space-y-2">
              <Label htmlFor="edit-title" className="text-xs font-medium">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-title"
                placeholder="Título da atividade"
                className="h-9 text-sm"
                {...form.register('title')}
              />
              {form.formState.errors.title && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            {/* Campo Descrição - RichTextEditor para notas, Textarea para outros */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                Descrição
              </Label>
              
              {config.showRichText ? (
              <RichTextEditor
                  key={`edit-${activity.id}-${open}`}
                  content={activity.description || ''}
                  onChange={setRichDescription}
                  placeholder="Adicione detalhes sobre a atividade..."
                  minHeight="180px"
                />
              ) : (
                <Textarea
                  id="edit-description"
                  placeholder="Adicione detalhes sobre a atividade..."
                  className="min-h-[100px] text-sm resize-none"
                  {...form.register('description')}
                />
              )}
            </div>

            {/* Campo Data/Hora (apenas para meeting e task) */}
            {/* Anexos existentes (somente leitura) */}
            {activity?.attachments && activity.attachments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium">
                  Anexos ({activity.attachments.length})
                </Label>
                <ActivityAttachmentsDisplay attachments={activity.attachments} />
              </div>
            )}

            {/* v1.1.0 — Notas nunca exibem campo de data (sem ciclo de conclusão) */}
            {activity.activity_type !== 'note' && (config.showSchedule || form.watch('scheduled_at')) && (
              <div className="space-y-2">
                <Label htmlFor="edit-scheduled_at" className="text-xs font-medium">
                  Data e Hora
                </Label>
                <Input
                  id="edit-scheduled_at"
                  type="datetime-local"
                  className="h-9 text-sm"
                  {...form.register('scheduled_at')}
                />
              </div>
            )}
          </form>
        </ScrollArea>

        {/* Footer com botão */}
        <div className="p-4 pt-3 border-t">
          <Button 
            type="submit"
            form="activity-edit-form"
            className="w-full h-9 text-sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar Alterações
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
